import axios from 'axios';
import lodashGet from 'lodash/get';
import {
  getProjectId,
  getDocsCurrentProject,
  getApigeeHost,
  getEnv,
  getRegion,
  getCurrentProjectScope,
  getCurrentPreviewIssueAttributes,
} from '../selectors';
import {
  closeDocumentSelectionModal,
  uploadAttachmentFileStart,
  uploadAttachmentFinished,
  uploadAttachmentFailed,
  updateUploadAttachmentProgress,
} from '../actions';
import { ENV_CONFIGS } from '../../constants/envs';
import { attachmentOrigin, OSS_URN_TYPE } from '../../constants/attachment-types';
import { getToken } from '../../utils/getToken';
import { getACMHeaders } from '../../utils/acm-helpers';
import {
  completeUploadFileToS3,
  getS3UploadUrl,
  uploadFileToS3,
} from '../../utils/getS3UrlFromOss';
import { getTargetVirtualFolderUrn } from './helpers/attachments';

const createStorageRequest = (fileName, apigeeHost, env, region, tokenProvider, projectId) => {
  return tokenProvider().then((token) =>
    axios.request({
      url: `${apigeeHost}${ENV_CONFIGS[region][env].DM_V2}/projects/${projectId}/storage`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: {
        fileName,
      },
    })
  );
};

const uploadFileToOss = (
  file,
  fileId,
  storageResponse,
  apigeeHost,
  tokenProvider,
  dispatch,
  projectScope
) => {
  const storageUrn = storageResponse.data.objectId;
  // prepare data for the upload file endpoint in oss api
  const data = {
    ossBucketKey: storageUrn.substring(
      storageUrn.lastIndexOf(':') + 1,
      storageUrn.lastIndexOf('/')
    ),
    ossObjectKey: storageUrn.substring(storageUrn.lastIndexOf('/') + 1, storageUrn.length),
  };

  const ossUrl = `${apigeeHost}/oss/v2/buckets/${data.ossBucketKey}/objects/${data.ossObjectKey}`;

  const results = {};
  return tokenProvider()
    .then((token) => {
      results.token = token;
      return getS3UploadUrl({ url: ossUrl, token, projectScope });
    })
    .then(({ urls, uploadKey }) => {
      results.uploadKey = uploadKey;
      const [s3Url] = urls;
      return uploadFileToS3({
        url: s3Url,
        file,
        fileId,
        projectScope,
        onUploadProgress: (progressEvent) => {
          dispatch(
            updateUploadAttachmentProgress(
              fileId,
              (100 * progressEvent.loaded) / progressEvent.total
            )
          );
        },
      });
    })
    .then(({ headers }) => {
      const { etag } = headers;
      const eTags = [JSON.parse(etag)];
      const { token, uploadKey } = results;
      return completeUploadFileToS3({ url: ossUrl, eTags, token, uploadKey });
    });
};

const copyFileToOss = (
  sourceUrn,
  fileId,
  storageResponse,
  apigeeHost,
  tokenProvider,
  dispatch,
  projectScope
) => {
  const storageUrn = storageResponse.data.objectId;
  // prepare data for the upload file endpoint in oss api
  const source = {
    ossBucketKey: sourceUrn.substring(sourceUrn.lastIndexOf(':') + 1, sourceUrn.lastIndexOf('/')),
    ossObjectKey: sourceUrn.substring(sourceUrn.lastIndexOf('/') + 1, sourceUrn.length),
  };
  const target = {
    ossBucketKey: storageUrn.substring(
      storageUrn.lastIndexOf(':') + 1,
      storageUrn.lastIndexOf('/')
    ),
    ossObjectKey: storageUrn.substring(storageUrn.lastIndexOf('/') + 1, storageUrn.length),
  };

  return tokenProvider().then((token) =>
    axios.request({
      url: `${apigeeHost}/oss/v2/buckets/${target.ossBucketKey}/objects/${source.ossObjectKey}/copyto/${target.ossObjectKey}`,
      method: 'PUT',
      headers: {
        ...getACMHeaders(projectScope),
        'Content-Type': 'text/plain; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      },
      onUploadProgress: (progressEvent) => {
        dispatch(
          updateUploadAttachmentProgress(fileId, (100 * progressEvent.loaded) / progressEvent.total)
        );
      },
    })
  );
};

const getCommandsRequestData = (fileName, folderUrn, objectId) => {
  return {
    data: {
      type: 'commands',
      attributes: {
        extension: {
          type: 'commands:autodesk.core:Upload',
          version: '1.0.0',
        },
      },
      relationships: {
        resources: {
          data: [{ type: 'versions', id: '1' }],
        },
      },
    },
    included: [
      {
        type: 'items',
        id: '2',
        attributes: {
          extension: {
            type: 'items:autodesk.bim360:File',
            version: '1.0',
          },
        },
        relationships: {
          tip: {
            data: { type: 'versions', id: '1' },
          },
          parent: {
            data: { type: 'folders', id: folderUrn },
          },
        },
      },
      {
        type: 'versions',
        id: '1',
        attributes: {
          name: fileName,
          extension: {
            type: 'versions:autodesk.bim360:File',
            version: '1.0',
          },
        },
        relationships: {
          storage: {
            data: { type: 'objects', id: objectId },
          },
        },
      },
    ],
  };
};

const createCommandRequest = ({
  objectId,
  fileName,
  folderUrn,
  projectId,
  apigeeHost,
  env,
  region,
  tokenProvider,
}) => {
  const commandsRequestData = getCommandsRequestData(fileName, folderUrn, objectId);

  return tokenProvider().then((token) =>
    axios.request({
      url: `${apigeeHost}${ENV_CONFIGS[region][env].DM}/projects/${projectId}/commands`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Authorization: `Bearer ${token}`,
      },
      data: commandsRequestData,
    })
  );
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const monitorCommandStatus = (
  commandId,
  projectId,
  apigeeHost,
  env,
  region,
  tokenProvider,
  resolve,
  reject
) => {
  tokenProvider()
    .then((token) =>
      axios.request({
        url: `${apigeeHost}${ENV_CONFIGS[region][env].DM}/projects/${projectId}/commands/${commandId}`,
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Authorization: `Bearer ${token}`,
        },
      })
    )
    .then((commandStatusResponse) => {
      const { status } = commandStatusResponse.data.data.attributes;
      const commandsStatusCompleted = status === 'committed' || status === 'completed';
      if (commandsStatusCompleted) {
        resolve();
      } else if (status === 'failed') {
        reject();
      } else {
        sleep(1000).then(() =>
          monitorCommandStatus(
            commandId,
            projectId,
            apigeeHost,
            env,
            region,
            tokenProvider,
            resolve,
            reject
          )
        );
      }
    });
};

const createMonitorCommandStatusPromise = (
  commandResponse,
  projectId,
  apigeeHost,
  env,
  region,
  tokenProvider
) => {
  return new Promise((resolve, reject) => {
    // extract the id of the command, we use it later to query the command status
    const commandId = commandResponse.data.data.id;
    monitorCommandStatus(
      commandId,
      projectId,
      apigeeHost,
      env,
      region,
      tokenProvider,
      resolve,
      reject
    );
  });
};

export default class AttachmentUploadThunk {
  static uploadAttachmentFiles(
    fileWrappers,
    issueId,
    createAfterUpload,
    origin,
    virtualFolderUrnTarget = null
  ) {
    return (dispatch, getState) => {
      dispatch(closeDocumentSelectionModal());
      const state = getState();
      const apigeeHost = getApigeeHost(state);
      const env = getEnv(state);
      const region = getRegion(state);
      const projectId = getProjectId(state);
      const currentProject = getDocsCurrentProject(state);
      const projectScope = getCurrentProjectScope(state);
      const currentRfi = getCurrentPreviewIssueAttributes(state);

      const { photosFolderUrn } = currentProject;
      const { virtualFolderUrn: previewedRfiVirtualFolderUrn } = currentRfi;

      const virtualFolderUrn =
        origin === attachmentOrigin.CREATE_FORM
          ? virtualFolderUrnTarget
          : previewedRfiVirtualFolderUrn;

      const folderUrn = getTargetVirtualFolderUrn(virtualFolderUrn, photosFolderUrn);

      fileWrappers.forEach((fileWrapper) => {
        const fileId = fileWrapper.id;
        const { file } = fileWrapper;
        const fileName = fileWrapper.name || file.name;
        const sourceUrn = fileWrapper.urn;

        dispatch(uploadAttachmentFileStart(fileId, fileName, issueId, origin));

        const tokenProvider = () => getToken({ getState, dispatch });
        createStorageRequest(fileName, apigeeHost, env, region, tokenProvider, projectId)
          .then((storageResponse) => {
            const storageUrn = lodashGet(storageResponse, 'data.objectId');
            if (storageUrn) {
              const action = sourceUrn
                ? copyFileToOss(
                    sourceUrn,
                    fileId,
                    storageResponse,
                    apigeeHost,
                    tokenProvider,
                    dispatch,
                    projectScope
                  )
                : uploadFileToOss(
                    file,
                    fileId,
                    storageResponse,
                    apigeeHost,
                    tokenProvider,
                    dispatch,
                    projectScope
                  );
              action
                .then(() =>
                  createCommandRequest({
                    objectId: storageUrn,
                    fileName,
                    folderUrn,
                    projectId,
                    apigeeHost,
                    env,
                    region,
                    tokenProvider,
                  })
                    .then((commandResponse) =>
                      createMonitorCommandStatusPromise(
                        commandResponse,
                        projectId,
                        apigeeHost,
                        env,
                        region,
                        tokenProvider
                      )
                        .then(() => {
                          dispatch(
                            uploadAttachmentFinished(
                              issueId,
                              {
                                name: fileName,
                                urn: storageResponse.data.objectId,
                                urnType: OSS_URN_TYPE,
                              },
                              fileId,
                              createAfterUpload,
                              origin
                            )
                          );
                        })
                        .catch(() => {
                          dispatch(uploadAttachmentFailed(fileId));
                        })
                    )
                    .catch(() => {
                      dispatch(uploadAttachmentFailed(fileId));
                    })
                )
                .catch(() => {
                  dispatch(uploadAttachmentFailed(fileId));
                });
            } else {
              dispatch(uploadAttachmentFailed(fileId));
            }
          })
          .catch(() => {
            dispatch(uploadAttachmentFailed(fileId));
          });
      });
    };
  }
}
